import { useState, useCallback } from "react";
import { validateFile, importRecords } from "modules/lazo/services/lazoService";

export const useFormLazo = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [duplicateInDatabase, setDuplicateInDatabase] = useState([]);
  const [validationDetails, setValidationDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /**
   * Maneja la selección y validación del archivo
   */
  const handleFileChange = async (file) => {
    // Validar tipo de archivo
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: "Formato de archivo no válido. Solo se permiten .xlsx o .xls",
        severity: "error",
      });
      return;
    }

    // Validar tamaño (máximo 40MB)
    const maxSize = 40 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: "El archivo es demasiado grande. Máximo 40MB permitidos",
        severity: "error",
      });
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setValidating(true);

    try {
      const validationResult = await validateFile(file);

      setValidationDetails({
        totalRows: validationResult.totalRows || 0,
        validRows: validationResult.validCount || 0,
        invalidRows: validationResult.invalidCount || 0,
      });

      if (validationResult.isValid) {
        setValidationErrors([]);
        setDuplicateInFile([]);
        setDuplicateInDatabase([]);

        setSnackbar({
          open: true,
          message: `✅ Archivo validado: ${validationResult.validCount || 0} registros válidos de ${
            validationResult.totalRows || 0
          }`,
          severity: "success",
        });
      } else {
        setValidationErrors(validationResult.errors || []);
        setDuplicateInFile(validationResult.duplicateInFile || []);
        setDuplicateInDatabase(validationResult.duplicateInDatabase || []);

        let errorMessage = `❌ El archivo contiene ${validationResult.errors?.length || 0} errores`;

        if (validationResult.duplicateInDatabase?.length > 0) {
          errorMessage += `, ${validationResult.duplicateInDatabase.length} placas duplicadas en sistema`;
        }
        if (validationResult.duplicateInFile?.length > 0) {
          errorMessage += `, ${validationResult.duplicateInFile.length} placas duplicadas en archivo`;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error en validación:", error);
      setValidationErrors([error.message || "Error al validar el archivo"]);
      setSnackbar({
        open: true,
        message: "Error al validar el archivo: " + (error.message || "Error desconocido"),
        severity: "error",
      });
    } finally {
      setValidating(false);
    }
  };

  /**
   * Maneja la importación del archivo
   */
  const handleImport = async (onImportSuccess, onError) => {
    // Validaciones previas
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Seleccione un archivo para importar",
        severity: "error",
      });
      return;
    }

    if (validationErrors.length > 0) {
      setSnackbar({
        open: true,
        message: `Corrija los ${validationErrors.length} errores de validación antes de importar`,
        severity: "error",
      });
      return;
    }

    if (duplicateInDatabase.length > 0) {
      setSnackbar({
        open: true,
        message: `Elimine las ${duplicateInDatabase.length} placas duplicadas del archivo antes de importar`,
        severity: "error",
      });
      return;
    }

    setImporting(true);
    setShowConfirmModal(true);
    setImportResult(null);
    setImportProgress(0);

    try {
      const result = await importRecords(selectedFile, (progress) => {
        setImportProgress(progress);
      });

      setImportResult(result);

      if (result.success) {
        if (onImportSuccess) {
          onImportSuccess(result);
        }
        setSnackbar({
          open: true,
          message:
            result.message || `✅ Importados ${result.importedCount || 0} registros exitosamente`,
          severity: "success",
        });
      } else {
        // Mostrar errores detallados del servidor
        let errorMsg = result.error || "Error al importar el archivo";

        if (result.validationErrors && result.validationErrors.length > 0) {
          errorMsg += `: ${result.validationErrors.slice(0, 3).join(", ")}`;
          if (result.validationErrors.length > 3) {
            errorMsg += ` y ${result.validationErrors.length - 3} errores más`;
          }
        }

        if (onError) {
          onError(errorMsg);
        }
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error en importación:", error);
      const errorMsg = error.message || "Error al importar";
      setImportResult({ success: false, error: errorMsg });

      if (onError) {
        onError(errorMsg);
      }

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  /**
   * Cierra el modal de confirmación y limpia el estado si fue exitoso
   */
  const handleCloseModal = () => {
    setShowConfirmModal(false);

    if (importResult?.success) {
      // Limpiar el formulario después de una importación exitosa
      setSelectedFile(null);
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);
      setValidationDetails(null);
      setImportProgress(0);

      // Limpiar el input file
      const fileInput = document.getElementById("file-input");
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  /**
   * Limpia el archivo seleccionado y los estados de validación
   */
  const clearSelectedFile = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setImportProgress(0);

    const fileInput = document.getElementById("file-input");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  /**
   * Cierra el snackbar de notificaciones
   */
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /**
   * Resetea todo el estado del formulario
   */
  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setImporting(false);
    setImportResult(null);
    setShowConfirmModal(false);
    setValidationErrors([]);
    setValidating(false);
    setImportProgress(0);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setSnackbar({
      open: false,
      message: "",
      severity: "success",
    });

    const fileInput = document.getElementById("file-input");
    if (fileInput) {
      fileInput.value = "";
    }
  }, []);

  return {
    // Estados
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    validationDetails,

    // Acciones
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
    resetForm,
  };
};
